import { Clock, Color, type CurvePath, DoubleSide, MathUtils, Mesh, type SceneControl, ShaderMaterial, TubeGeometry, Vector2, type Vector3, use } from '@anov/3d-core'
import { BaseTraffic } from '../base'
import { fragmentShader } from './fragmentShader'
import { vertexShader } from './vertexShader'
import type { StreamerTrafficInterface } from '.'

type LanelineControl = {
  index: number
  direction?: number
}

type StreamerTrafficParams = {
  path: CurvePath<Vector3>
  width?: number
  divide?: number
  divideWidth?: number
  num?: number
  length?: number
  baseColor?: string
  speed?: number
  control?: LanelineControl[]
  corner?: number
}

/**
 * 流光风格交通组件
 */
export class StreamerTraffic extends BaseTraffic implements StreamerTrafficInterface {
  /** 流光速度 */
  private _speed: number

  /** 流光数量 */
  private _num: number

  /** 流光颜色 */
  private _baseColor: string

  /** 流光宽度 */
  private _divideWidth: number

  /** 流光长度 */
  private _length: number

  private _streamerArr: Mesh[] = []

  private _lanelineControl: LanelineControl[] = []

  private _control = []

  public get speed() {
    return this._speed
  }

  public get num() {
    return this._num
  }

  public get baseColor() {
    return this._baseColor
  }

  public get divideWidth() {
    return this._divideWidth
  }

  public get width(): number {
    return this._width
  }

  public set width(w: number) {
    this._width = w

    this.generateLaneLines()
    this.generateStreamer()
  }

  public get divide(): number {
    return this._divide
  }

  public set divide(num: number) {
    this._divide = num

    this.generateLaneLines()
    this.generateStreamer()
  }

  constructor(params: StreamerTrafficParams, sceneControl: SceneControl) {
    super(params, sceneControl)

    const { path, width = 4, divide = 4, divideWidth = 1, num = 5, length = 0.25, baseColor = '#ff7700', speed = 100, corner = 10, control = [] } = params

    this._path = path
    this._width = width
    this._divide = divide
    this._divideWidth = divideWidth / 2
    this._num = num
    this._baseColor = baseColor
    this._speed = speed
    this._corner = corner
    this._length = length
    this._lanelineControl = control

    this.generateLaneLines()
    this.generateStreamer()
  }

  /**
   * generate streamer
   */
  private generateStreamer() {
    this._streamerArr.forEach((mesh) => {
      mesh.removeFromParent()
    })
    this._streamerArr = []

    for (let i = 0; i < this.lanelines.length; i++) {
      const geometry = new TubeGeometry(this.lanelines[i], Math.floor(this.lanelines[i].getLength()) * 10, this._divideWidth, 5, false)
      const material = new ShaderMaterial({
        side: DoubleSide,
        transparent: true,
        uniforms: {
          uColor: { value: new Color(this._baseColor) },
          uTime: { value: MathUtils.lerp(-1, 1, Math.random()) },
          uFade: { value: new Vector2(0, this._length + Math.random() * this._length - this._length / 2) },
          uNumLines: { value: this._num },
          uOffset: { value: Math.random() },
          uAlpha: { value: Math.random() },
          uSpeed: { value: this._speed + Math.random() * this._speed - this._speed / 2 },
          ulength: { value: Math.floor(this.lanelines[i].getLength()) },
          uReverse: { value: this._lanelineControl[i]?.direction ?? 1.0 },
        },
        vertexShader,
        fragmentShader,
      })

      const road = new Mesh(geometry, material)

      // 渲染顺序
      road.renderOrder = 1000

      this.add(road)
      this.run(material)
      this._streamerArr.push(road)
    }
  }

  /**
   * run
   * @param material
   */
  private run(material) {
    const clock = new Clock()

    this._control.push(
      use.useframe(this.sceneControl, () => {
        material.uniforms.uTime.value = clock.getElapsedTime()
      }),
    )
  }

  override removeFromParent(): this {
    // todo: 升级three.js后，父对象变化，会触发此函数，重组内部引起关系
    this.parent && this._control.forEach((control) => {
      control()
    })

    return super.removeFromParent()
  }
}